// UTILS
import { nextTick } from "vue"
import { setup, createActor, fromPromise } from "xstate"
import { storeToRefs } from "pinia"
import router from "@/router"
import { useCookies } from "@vueuse/integrations/useCookies.js"
import { COOKIES } from "@/config/constants"
import { useSessionStorage } from "@/helpers/useSessionStorage"

// TYPES
import type { GlobalMachineEvent } from "@/machines/types"
import type { VoiceMachineEventType } from "./types"

// STORES
import { useUserStore } from "@/stores/user"
import { useI18nStore } from "@/stores/i18n"
import { useAnswersStore } from "@/features/voice/store/answers.store"
import { useQuestionsStore } from "@/features/voice/store/questions.store"

// MACHINE
import { globalActor } from "@/machines/globalMachine"
import { withAssessmentLoadingMinDuration } from "@/features/assessment-loading/assessmentLoadingDuration"

// Loader for Voice datas
// to redirect to Intro or WelcomeBack
const loadVoiceData = fromPromise(() =>
  withAssessmentLoadingMinDuration("VOICE", async () => {
    const { fetchVoice } = useAnswersStore()
    const voice = await fetchVoice()
    const { fetchQuestions, getNextQuestion } = useQuestionsStore()

    const { showLang } = storeToRefs(useI18nStore())

    const assessmentSummary = useUserStore().assessmentSummary.assessments.swipe
    const isFinisedAndNotPassed =
      voice?.status === "finish" && assessmentSummary?.not_passed

    // Legacy, the test has been not_passed and considered with status finish in assessment summary (old) but not in workflow and next_assessment (new)
    let assessmentStatus = isFinisedAndNotPassed ? "todo" : voice?.status

    // The language can only be changed as long as the test is not started
    showLang.value = assessmentStatus !== "inprogress"

    // status can be "todo", "inprogress" or "finish"
    switch (assessmentStatus) {
      case "todo":
        await fetchQuestions()
        await getNextQuestion()
        return { status: "new" }

      case "inprogress":
        await fetchQuestions()
        await getNextQuestion()
        return { status: "showQuestions" }

      case "redirect-contact":
        globalActor.send({ type: "GLOBAL.GUEST.CONTACTSUPPORT" })
        return

      case "finish":
        return { status: "showFinal" }

      default:
        await fetchQuestions()
        await getNextQuestion()
        return { status: "new" }
    }
  })
)

type MicrophonePermissionCheckResult = {
  isGranted: boolean
}

function canQueryMicrophonePermission() {
  return (
    typeof navigator !== "undefined" &&
    typeof navigator.permissions?.query === "function"
  )
}

const checkMicrophonePermission = fromPromise(
  async (): Promise<MicrophonePermissionCheckResult> => {
    if (!canQueryMicrophonePermission()) {
      return { isGranted: false }
    }

    try {
      const result = await navigator.permissions.query({
        name: "microphone" as PermissionName,
      })

      return { isGranted: result.state === "granted" }
    } catch {
      return { isGranted: false }
    }
  }
)

export const voiceMachine = setup({
  actors: {
    loadVoiceData,
    checkMicrophonePermission,
  },
  types: {} as {
    events: VoiceMachineEventType
  },
  actions: {},
}).createMachine({
  id: "voiceMachine",
  initial: "loading",
  states: {
    loading: {
      entry: [
        () => {
          if (router.currentRoute.value.name !== "voiceLoading") {
            router.replace({ name: "voiceLoading" })
          }
        },
      ],
      invoke: {
        id: "loadVoice",
        src: "loadVoiceData",
        onDone: [
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status === "new",
            target: "microphonePermissionCheck",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status === "showQuestions",
            target: "welcomeBack",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status ===
              "showLoadResults",
            target: "loadResults",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status === "showFinal",
            target: "final",
          },
        ],
      },
    },
    welcomeBack: {
      entry: [() => router.replace({ name: "voiceWelcomeBack" })],
      on: {
        done: { target: "authorize" },
        test: { target: "test" },
      },
    },
    microphonePermissionCheck: {
      invoke: {
        id: "checkMicrophonePermission",
        src: "checkMicrophonePermission",
        onDone: [
          {
            guard: ({ event }) =>
              (event.output as MicrophonePermissionCheckResult).isGranted,
            target: "tuto",
          },
          { target: "authorize" },
        ],
        onError: { target: "authorize" },
      },
    },
    authorize: {
      entry: [() => router.replace({ name: "voiceAuthorize" })],
      on: {
        done: { target: "tuto" },
        test: { target: "test" },
        refuse: { target: "authorizeRefused" },
      },
    },
    authorizeRefused: {
      entry: [() => router.replace({ name: "voiceAuthorizeRefused" })],
      on: {
        back: { target: "authorize" },
        done: { target: "final" },
      },
    },
    tuto: {
      entry: [() => router.replace({ name: "voiceTuto" })],
      on: {
        done: { target: "test" },
        retry: { target: "authorize" },
      },
    },
    test: {
      entry: [() => router.replace({ name: "voiceTest" })],
      on: {
        done: { target: "feedback" },
      },
    },
    feedback: {
      entry: [() => router.replace({ name: "voiceFeedback" })],
      on: {
        done: { target: "end" },
      },
    },
    end: {
      entry: [() => router.replace({ name: "voiceEnd" })],
      on: {
        done: { target: "loadResults" },
      },
    },
    loadResults: {
      entry: [() => router.replace({ name: "voiceLoadResults" })],
      on: {
        done: { target: "final" },
      },
    },

    final: {
      type: "final",
    },
  },
})

export let voiceActor = createActor(voiceMachine)

const voiceActorObserver = {
  async complete() {
    const { isFromInt } = storeToRefs(useUserStore())
    const { getSessionItem, deleteSessionItem } = useSessionStorage()

    const { fetchNextAssessment } = useUserStore()
    const { nextAssessmentName } = storeToRefs(useUserStore())

    const workflow_uuid = useCookies().get(COOKIES.WORKFLOW_UUID)
    await fetchNextAssessment(workflow_uuid ?? null)

    await nextTick()

    if (nextAssessmentName.value) {
      globalActor.send({
        type: `GLOBAL.REDIRECT.${nextAssessmentName.value.toUpperCase()}` as GlobalMachineEvent,
      })
    } else {
      useCookies().remove(COOKIES.WORKFLOW_UUID, COOKIES.OPTIONS)
      useCookies().remove(COOKIES.PM_UUID, COOKIES.OPTIONS)
      useCookies().remove(COOKIES.COMPANY_UUID, COOKIES.OPTIONS)

      if (isFromInt.value) {
        if (getSessionItem("goto_param")) {
          deleteSessionItem("goto_param")
        }
        if (getSessionItem("redirect_url")) {
          let redirectUrl = getSessionItem("redirect_url") as string
          deleteSessionItem("redirect_url")
          // Redirect to custom page
          window.location.href = redirectUrl
          return
        } else {
          // Redirect to thanksATS route
          router.push({ name: "thanksATS" })
          return
        }
      }

      globalActor.send({ type: "GLOBAL.VOICE.DONE" })
    }
  },
}

voiceActor.subscribe(voiceActorObserver)

/**
 * Observateurs extérieurs, réabonnés à chaque acteur neuf.
 *
 * Sans ça, un abonné qui garde la référence de l'acteur — le store d'aide, qui
 * en tire le nom de l'assessment courant, donc les CTA du header — resterait
 * branché sur l'acteur remplacé et deviendrait sourd.
 */
const voiceActorObservers: Array<(_snapshot: unknown) => void> = []

export function subscribeToVoiceActor(observer: (_snapshot: unknown) => void) {
  voiceActorObservers.push(observer)
  voiceActor.subscribe(observer)
}

let hasVoiceActorStarted = false

/**
 * XState ignore `start()` sur un acteur déjà démarré (ou arrivé à son état
 * final) : aucune action d'entrée ne rejoue, la machine ne réapplique donc
 * jamais sa route et l'utilisateur reste sur la route parente nue, écran
 * blanc. Le cas se produit dès qu'on entre deux fois dans l'assessment au
 * cours de la même page — sortie par « non passé », ou renvoi dessus par le
 * workflow. On repart donc d'un acteur neuf.
 *
 * Mais UNIQUEMENT en entrant vraiment dans l'assessment, c'est-à-dire sur la
 * route parente nue, celle par laquelle la machine globale y fait entrer. Le
 * composant racine est aussi remonté sans qu'on change d'assessment — App.vue
 * reconstruit tout `main` quand la langue change — et repartir de zéro là
 * relancerait le test depuis son écran de chargement.
 */
export function startVoiceActor() {
  const isEnteringAssessment = router.currentRoute.value.name === "voice"

  if (hasVoiceActorStarted && isEnteringAssessment) {
    voiceActor.stop()
    voiceActor = createActor(voiceMachine)
    voiceActor.subscribe(voiceActorObserver)
    for (const observer of voiceActorObservers) voiceActor.subscribe(observer)
  }

  hasVoiceActorStarted = true
  voiceActor.start()
}
